/**
 * Data layer for `automation-crud-permission-scope`. Thin typed fetch wrappers over /api/v1/automation-crud-permission-scope.
 * Roles × scope (resource × action) permission grid. Raw `fetch()` is confined to this file per the dash contract.
 */

export type PermissionAction = "read" | "write" | "delete" | "execute";

export interface RoleScopeRow {
	id: string;
	tenant_id: string;
	role_name: string;
	inherited_role_id: string | null;
	scope_json: Record<string, Partial<Record<PermissionAction, boolean>>>;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListRoleScopeResponse {
	count: number;
	items: RoleScopeRow[];
}

export interface CreateRoleScopeBody {
	role_name: string;
	inherited_role_id?: string | null;
	scope_json?: Record<string, Partial<Record<PermissionAction, boolean>>>;
}

export interface UpdateRoleScopeBody {
	scope_json?: Record<string, Partial<Record<PermissionAction, boolean>>>;
	inherited_role_id?: string | null;
	role_name?: string;
}

const BASE = "/api/v1/automation-crud-permission-scope";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListRoleScopeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRoleScopeResponse>(res);
}

export async function create(body: CreateRoleScopeBody): Promise<RoleScopeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<RoleScopeRow>(res);
}

export async function update(id: string, body: UpdateRoleScopeBody): Promise<RoleScopeRow> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<RoleScopeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const automationCrudPermissionScopeQueryKeys = {
	all: ["automation-crud-permission-scope"] as const,
	list: () => [...automationCrudPermissionScopeQueryKeys.all] as const,
	detail: (id: string) => [...automationCrudPermissionScopeQueryKeys.all, id] as const,
};
